import React from "react"

import classNames from "classnames"

import { useMergedRefs } from "shared/lib/useMergedRefs"
import { Action } from "shared/types/Action"
import { ProductPosition } from "shared/types/ProductPosition"
import { RowRendererProps } from "shared/ui/table/types"

import { useProductPositionTableRowPayload } from "widgets/product-position-table/contexts/ProductPositionTableRowPayloadContext"
import { ProductPositionRowCellType } from "widgets/product-position-table/hooks/useProductPositionRowCells"

import { ProductPositionTableCell } from "../../ProductPositionTableCell"
import { ProductPositionTableRowCollapseButton } from "../../ProductPositionTableRowCollapseButton"
import { ProductPositionTableRowWrapper } from "../../ProductPositionTableRowWrapper"
import { ProductPositionRowAddonsCell } from "../cells/ProductPositionRowAddonsCell"
import { ProductPositionRowClarificationRequestCell } from "../cells/ProductPositionRowClarificationRequestCell"
import { ProductPositionRowDimensioningCell } from "../cells/ProductPositionRowDimensioningCell"
import {
  ProductPositionRowMenuCell,
  ProductPositionRowMenuCellHandle
} from "../cells/ProductPositionRowMenuCell"
import { ProductPositionRowPositionCell } from "../cells/ProductPositionRowPositionCell"
import { ProductPositionRowQuantityCell } from "../cells/ProductPositionRowQuantityCell"
import { ProductPositionRowRemarkCell } from "../cells/ProductPositionRowRemarkCell"
import { ProductPositionRowSelectionCell } from "../cells/ProductPositionRowSelectionCell"
import { ProductPositionRowStatusCell } from "../cells/ProductPositionRowStatusCell"
import { ProductPositionRowTitleCell } from "../cells/ProductPositionRowTitleCell"
import { ProductPositionRowVariantKeyCell } from "../cells/ProductPositionRowVariantKeyCell"
import { EnhancedProductPositionTableRowPayload } from "./ProductPositionTableProductPositionRow"

export interface ProductPositionRowCellProps {
  isTooltipDisabled?: boolean
}

export interface ProductPositionTableProductPositionRowViewModel
  extends Omit<RowRendererProps<ProductPosition>, "onCollapse">,
    EnhancedProductPositionTableRowPayload {
  isPending: boolean
  displayTitle: string | null | undefined
  position: string
  variantKey: string
  style: React.CSSProperties
  hasChildren: boolean
  showClarificationRequestColumn?: boolean
  onCollapse: React.MouseEventHandler
  actions: Action[]
  rowRef: React.RefObject<HTMLDivElement | null>
  menuCellRef: React.RefObject<ProductPositionRowMenuCellHandle | null>
}

export const ProductPositionTableProductPositionRowView = React.memo(
  ({
    item,
    index,
    isSelected,
    isDragDisabled,
    draggedElementID,
    dragHandleRef,
    rowElementProps,
    isFocused,
    variantKey,
    style,
    displayTitle,
    position,
    isCollapsed,
    isPending,
    hasChildren,
    showClarificationRequestColumn,
    onCollapse,
    getCellProps,
    isTitleLoading,
    isValid,
    isLoadingValidity,
    productInformation,
    actions,
    rowRef,
    menuCellRef
  }: ProductPositionTableProductPositionRowViewModel) => {
    const { hideQuantity, showSelectionCheckbox, columnPreset } =
      useProductPositionTableRowPayload()

    const isSelection = columnPreset === "selection"

    const mergedRowRef = useMergedRefs(
      rowRef,
      rowElementProps?.ref,
      dragHandleRef
    )

    return (
      <ProductPositionTableRowWrapper
        {...rowElementProps}
        role="row"
        ref={mergedRowRef}
        index={index}
        style={style}
        isDragDisabled={isDragDisabled || !!draggedElementID}
        data-key={variantKey}
        data-rowtype={item.__typename}
        className={classNames(
          rowElementProps?.className,
          "product-position",
          isSelected ? "highlight" : undefined,
          draggedElementID ? "disable-hover" : undefined
        )}
      >
        <div role="gridcell" aria-hidden className="table-row-cell left-space">
          <div className="project-table-row__select-indicator" />
        </div>
        {showSelectionCheckbox ? (
          <ProductPositionRowSelectionCell
            isSelected={isSelected}
            label={displayTitle ?? item.variantKey}
          />
        ) : null}
        <ProductPositionTableCell aria-hidden className="collapse-button">
          {hasChildren ? (
            <ProductPositionTableRowCollapseButton
              item={item}
              isCollapsed={isCollapsed}
              onCollapse={onCollapse}
              isLoading={isPending}
            />
          ) : null}
        </ProductPositionTableCell>
        <ProductPositionRowPositionCell
          {...getCellProps?.(ProductPositionRowCellType.POSITION)}
          position={position}
        />
        {showClarificationRequestColumn ? (
          <ProductPositionRowClarificationRequestCell
            {...getCellProps?.(
              ProductPositionRowCellType.CLARIFICATION_REQUEST
            )}
            hasClarificationRequest={item.hasClarificationRequest}
          />
        ) : null}
        <ProductPositionRowTitleCell
          {...getCellProps?.(ProductPositionRowCellType.NAME)}
          title={displayTitle}
          isLoading={isTitleLoading}
        />
        <ProductPositionRowVariantKeyCell
          {...getCellProps?.(ProductPositionRowCellType.VARIANT_KEY)}
          variantKey={item.variantKey}
        />
        {isSelection ? null : (
          <ProductPositionRowQuantityCell
            {...getCellProps?.(ProductPositionRowCellType.QUANTITY)}
            quantity={item.quantity}
            isHidden={hideQuantity}
          />
        )}
        {isSelection ? null : (
          <ProductPositionRowDimensioningCell
            {...getCellProps?.(ProductPositionRowCellType.DIMENSIONING)}
            productInformation={productInformation}
          />
        )}
        {isSelection ? null : (
          <ProductPositionRowRemarkCell
            {...getCellProps?.(ProductPositionRowCellType.REMARK)}
            remark={item.remark}
          />
        )}
        {isSelection ? null : (
          <ProductPositionRowAddonsCell
            {...getCellProps?.(ProductPositionRowCellType.ADDONS)}
            productInformation={productInformation}
          />
        )}
        {isSelection ? null : (
          <ProductPositionRowStatusCell
            {...getCellProps?.(ProductPositionRowCellType.STATUS)}
            isValid={isValid}
            isLoading={isLoadingValidity}
          />
        )}
        {isSelection ? null : (
          <ProductPositionRowMenuCell
            ref={menuCellRef}
            actions={actions}
            isFocused={isFocused}
            cellProps={getCellProps?.(ProductPositionRowCellType.MENU)}
          />
        )}
        <div
          role="gridcell"
          aria-hidden
          className="table-row-cell right-space"
        />
      </ProductPositionTableRowWrapper>
    )
  }
)
